import { z } from 'zod';
/**
 * Agent action - sends message to agent with instruction and metadata
 *
 * AgentActions are generic and portable - they can be defined by:
 * - Components (hard-coded actions like "More like this")
 * - Workflows (template-defined actions)
 * - Plans (runtime-injected actions)
 * - Agents (future: agent-suggested actions)
 *
 * AgentActions are stored in artifact content and converted to runtime actions
 * by the artifact component, which adds a handler that:
 * 1. Resolves template variables in the instruction
 * 2. Sends the message to the agent via the conversation context
 */
export const AgentActionSchema = z.object({
    /** Action type discriminator */
    type: z.literal('agent'),
    /** Unique identifier for this action */
    id: z.string(),
    /** Display label for the action button */
    label: z.string(),
    /** Optional Lucide icon name */
    icon: z.string().optional(),
    /** Visual variant for the action button */
    variant: z.enum(['default', 'primary', 'destructive']).default('default'),
    /** Optional description for tooltips */
    description: z.string().optional(),
    /** Whether action is disabled (static boolean for agent actions) */
    disabled: z.boolean().optional(),
    /**
     * Agent instruction (supports template variables)
     * Templates: {{selectedItem.title}}, {{planId}}, {{stepId}}, etc.
     * Example: "User selected '{{selectedItem.title}}'. Complete step {{stepId}} and move to next step."
     */
    instruction: z.string(),
    /**
     * Metadata to include with the message
     * Can include plan context, workflow context, etc.
     */
    metadata: z.record(z.any()).default({}),
    /**
     * Action scope - determines when and how the action is displayed
     * - 'item': Action applies to a selected item (requires selection)
     * - 'artifact': Action applies to the entire artifact (always visible)
     * - 'both': Action works in both contexts (visible always, context varies)
     */
    scope: z.enum(['item', 'artifact', 'both']).default('item'),
    /** Optional tracking of where this action originated (for debugging) */
    source: z.enum(['component', 'workflow', 'plan', 'agent']).optional(),
    /** Sort order (lower numbers appear first) */
    order: z.number().default(100)
});
/**
 * Helper to create an AgentAction with type safety
 */
export function createAgentAction(action) {
    return {
        type: 'agent',
        ...action
    };
}
//# sourceMappingURL=agentAction.js.map